import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import {
  getPollBuilderQuestions,
  getPollResults,
  getPollResultsAllChoiceById,
  getPollResultsChoiceData,
  getUserData,
} from '../../../shared/redux/selectors';
import splitNumberIntoDigits from '../../../shared/helpers/numbers';
import { electionUrl } from '../../../../globals';
import { filterPollBuilderQuestionsByLang } from '../helpers/pollBuilder';
import { isEmailable } from '../helpers/constitutionResults';
import { RouterButton } from '../../../shared/components/Button';
import FooterPollResultsItem from './FooterPollResultsItem';
import Loading from '../../../shared/components/Loading';
import PollResultItem from './PollResultItem';
import Section from '../../../shared/components/Section';
import Separator from '../../../shared/structure/Separator';
import OneCol from '../../../shared/structure/OneCol';
import Paragraph from '../../../shared/components/Paragraph';
import VoiceLink from '../../../shared/components/Link';

function VoteResultsContainer({
  questions, pollResults, getSelectedAll, getChoiceData, userData,
}) {
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.language || window.localStorage.i18nextLng;

  if (!questions || !pollResults) {
    return <Loading />;
  }

  const questionsLangData = filterPollBuilderQuestionsByLang(questions, selectedLanguage);

  const showEmailButton = isEmailable(userData);

  const pollResultsData = [];

  const footer = (id, choiceId, userSelected) => {
    let userChoice = '';
    let totalResultsPercent = '';
    let totalResultsNumber = '';
    if (choiceId === userSelected) {
      userChoice += t('poll_results_user_choice');
    }
    const data = getChoiceData(id, choiceId);

    if (!data) return '';

    const { percent, amount } = data;
    totalResultsPercent += percent && amount && ` ${data.percent}% `;
    totalResultsNumber += percent && amount && `/ ${splitNumberIntoDigits(data.amount)}\u00A0${t('poll_results_people')}`;

    return (
      <FooterPollResultsItem
        userChoice={userChoice}
        totalResultsPercent={totalResultsPercent}
        totalResultsNumber={totalResultsNumber}
      />
    );
  };

  questionsLangData.forEach((data, i) => {
    const { id } = data;

    const selected = getSelectedAll(id);
    const userSelected = userData && userData[id];

    const dataWithFooter = {
      ...data,
      choices: data.choices.map((choice) => ({
        ...choice,
        footer: footer(id, choice.id, userSelected),
      })),
    };

    pollResultsData.push((
      <Section
        key={id}
        content={(
          <>
            {i === 0 && (
              <Paragraph>{t('poll_res_first_desc')}</Paragraph>
            )}
            <PollResultItem
              {...dataWithFooter}
              small={i !== 0}
              selected={selected}
              userSelected={userSelected}
            />
          </>
        )}
      />
    ));

    if (i < questionsLangData.length - 1) {
      pollResultsData.push((
        <Separator
          key={`separator_${data.id}`}
          background="gray"
        />
      ));
    }
  });

  return (
    <>
      {!pollResults ? (
        <Loading />
      ) : (
        <>
          <Section
            background="blue"
            title={t('poll_results_call_header')}
          />

          {showEmailButton ? (
            <Section
              background="gray"
              title={t('poll_results_title')}
              button={(
                <RouterButton
                  background="blue"
                  text={t('poll_results_button')}
                  to="/mail"
                />
              )}
            />
          ) : (
            <Section
              background="gray"
              content={(
                <>
                  <Paragraph
                    title={t('poll_results_disclaimer')}
                  >
                    {t('poll_results_disclaimer_p_1_start')}
                    <VoiceLink
                      color="primary"
                      target="_blank"
                      href="http://bit.ly/golos-final-report"
                    >
                      {t('poll_results_disclaimer_p_1_link_1')}
                    </VoiceLink>
                    {t('poll_results_disclaimer_p_1_middle')}
                    <VoiceLink
                      color="primary"
                      target="_blank"
                      href={electionUrl}
                    >
                      {t('poll_results_disclaimer_p_1_link_2')}
                    </VoiceLink>
                  </Paragraph>
                  <Paragraph>
                    {t('poll_results_disclaimer_p_2')}
                  </Paragraph>
                  <Paragraph>
                    {t('poll_results_disclaimer_p_3')}
                  </Paragraph>
                  <Paragraph>
                    {t('poll_results_disclaimer_p_4')}
                  </Paragraph>
                </>
              )}
            />
          )}

          {pollResultsData}

          {userData && userData.proposals && (
            <>
              <Separator
                background="gray"
              />
              <Section
                title={t('poll_results_offers_title')}
                content={(
                  <>
                    <Paragraph>
                      {userData.proposals}
                    </Paragraph>
                  </>
                )}
              />
            </>
          )}

          {showEmailButton && (
            <>
              <Separator
                background="blue"
              />

              <Section
                background="white"
                content={(
                  <OneCol
                    span={12}
                    textAlign="right"
                    col={(
                      <RouterButton
                        background="blue"
                        text={t('poll_results_button')}
                        to="/mail"
                      />
                    )}
                  />
                )}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

VoteResultsContainer.defaultProps = {
  questions: null,
  pollResults: null,
  userData: null,
};

VoteResultsContainer.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.any),
  pollResults: PropTypes.objectOf(PropTypes.any),
  getSelectedAll: PropTypes.func.isRequired,
  getChoiceData: PropTypes.func.isRequired,
  userData: PropTypes.objectOf(PropTypes.any),
};

const mapStateToProps = (state) => ({
  questions: getPollBuilderQuestions(state),
  pollResults: getPollResults(state),
  getSelectedAll: getPollResultsAllChoiceById(state),
  getChoiceData: getPollResultsChoiceData(state),
  userData: getUserData(state),
});

export default connect(mapStateToProps)(VoteResultsContainer);
